import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, Car, CreditCard, BarChart3, ShieldCheck, Loader2, Download, Truck, Eye, FileSpreadsheet, Phone, KeyRound, Sparkles, Wallet, Hourglass, ShieldAlert, Zap, Building2, Bell } from "lucide-react";
import { Button } from "../components/ui/button";
import ExportButton from "../components/ExportButton";
import TFTPaymentRequestsPanel from "../components/admin/TFTPaymentRequestsPanel";
import PasswordResetsPanel from "../components/admin/PasswordResetsPanel";
import StatsPanel from "../components/admin/StatsPanel";
import VisitStatsPanel from "../components/admin/VisitStatsPanel";
import PromoSettingsPanel from "../components/admin/PromoSettingsPanel";
import AdminPromoCodesPanel from "../components/admin/AdminPromoCodesPanel";
import AdminGodsEye from "../components/admin/AdminGodsEye";
import PaymentMethodsPanel from "../components/admin/PaymentMethodsPanel";
import AdminPackRequests from "../components/admin/AdminPackRequests";
import AdminTFTStats from "../components/admin/AdminTFTStats";
import AdminImpersonationLogs from "../components/admin/AdminImpersonationLogs";
import AdminPackCatalog from "../components/admin/AdminPackCatalog";
import AdminBroadcastPanel from "../components/admin/AdminBroadcastPanel";
import AdminReferralLeaderboard from "../components/admin/AdminReferralLeaderboard";
import PackBookingsReport from "../components/PackBookingsReport";
import DriverManagement from "../components/admin/DriverManagement";
import VehicleManagement from "../components/admin/VehicleManagement";
import { UserFilters, UsersTable, DeleteConfirmDialog } from "../components/admin/UserManagement";
import { BookingFilters, BookingsList, PaymentFilters, PaymentsTable } from "../components/admin/BookingsPayments";
import PromoLostCommissionKPI from "../components/admin/PromoLostCommissionKPI";
import NotificationBell from "../components/NotificationBell";
import NotificationsActivateButton from "../components/NotificationsActivateButton";
import NotificationSoundSettings from "../components/NotificationSoundSettings";
import ExpressAdminPanel from "../components/admin/ExpressAdminPanel";
import ExpressDriversPanel from "../components/admin/ExpressDriversPanel";
import AdminPartnersPanel from "../components/admin/AdminPartnersPanel";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useAdminLiveAlerts } from "../hooks/useAdminLiveAlerts";

function log(...args) {
  if (process.env.NODE_ENV === "development") console.error(...args);
}

export default function AdminDashboard() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.is_super_admin === true;
  usePushNotifications(currentUser?.id, currentUser?.role);
  // Read initial tab from URL (?tab=xxx) so links from other pages can deep-link.
  const initialTab = (() => {
    if (typeof window === "undefined") return "stats";
    return new URLSearchParams(window.location.search).get("tab") || "stats";
  })();
  const [tab, setTab] = useState(initialTab);
  const { counts: liveAlerts } = useAdminLiveAlerts(currentUser?.id, tab);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(50);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [bookingSearch, setBookingSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Admin impersonation: open the driver dashboard in a NEW tab with a short-lived
  // impersonation token. Admin's own session in the current tab is preserved
  // untouched (sessionStorage is per-tab).
  const handleImpersonate = async (driver) => {
    if (!driver?.id) return;
    if (!window.confirm(`Vous allez ouvrir la session de ${driver.name} (${driver.email}) dans un nouvel onglet pour effectuer une action corrective. Continuer ?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${driver.id}/impersonate`);
      const url = new URL(window.location.origin + "/impersonate");
      url.searchParams.set("t", data.token);
      url.searchParams.set("name", driver.name || "");
      window.open(url.toString(), "_blank");
    } catch (e) {
      alert(e.response?.data?.detail || "Impossible d'ouvrir la session");
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Promise.allSettled([
        api.get("/admin/stats"), api.get("/bookings"),
        api.get("/payments"), api.get("/admin/pending-drivers"), api.get("/admin/all-drivers"),
        api.get("/admin/vehicles"),
      ]);
      const [s, b, p, pd, ad, av] = res.map((r) => (r.status === "fulfilled" ? r.value.data : null));
      res.filter((r) => r.status === "rejected").forEach((r) => log("Failed to fetch admin data:", r.reason?.message));
      if (s) setStats(s);
      setBookings(b || []); setPayments(p || []); setPendingDrivers(pd || []); setAllDrivers(ad || []); setAllVehicles(av || []);
    } catch (e) {
      log("Failed to fetch admin data:", e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchUsers = useCallback(async (page = usersPage, pageSize = usersPageSize, q = userFilter, role = userRoleFilter) => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/admin/users", {
        params: { page, page_size: pageSize, q, role },
      });
      // New paginated shape
      if (data && Array.isArray(data.items)) {
        setUsers(data.items);
        setUsersTotal(data.total || 0);
        setUsersTotalPages(data.total_pages || 0);
      } else if (Array.isArray(data)) {
        // Fallback (legacy)
        setUsers(data);
        setUsersTotal(data.length);
        setUsersTotalPages(1);
      }
    } catch (e) { log("Failed to fetch users:", e.message); }
    finally { setUsersLoading(false); }
  }, [usersPage, usersPageSize, userFilter, userRoleFilter]);

  // Reset to page 1 when search/role filter changes; debounce search input.
  useEffect(() => { setUsersPage(1); }, [userFilter, userRoleFilter]);

  // Fetch users whenever the users tab becomes active or filters/page change.
  useEffect(() => {
    if (tab !== "users") return;
    const handle = setTimeout(() => {
      fetchUsers(usersPage, usersPageSize, userFilter, userRoleFilter);
    }, userFilter ? 300 : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, usersPage, usersPageSize, userFilter, userRoleFilter]);

  const toggleUser = useCallback(async (userId, isActive) => {
    try {
      await api.patch(`/admin/users/${userId}`, { is_active: !isActive });
      fetchUsers();
    } catch (err) { log("Failed to toggle user:", err.message); }
  }, [fetchUsers]);

  const deleteUser = useCallback(async (userId) => {
    setActionLoading(true);
    try {
      await api.delete(`/admin/users/${userId}`);
      setDeleteConfirm(null);
      fetchUsers();
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(false); }
  }, [fetchUsers, fetchAll]);

  const validateDriver = useCallback(async (driverId, action, reason = "") => {
    setActionLoading(true);
    try {
      await api.post(`/admin/validate-driver/${driverId}`, { action, reason });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setActionLoading(false); }
  }, [fetchAll]);

  const exportData = useCallback(async (collection, format = "csv") => {
    try {
      const response = await api.get(`/admin/export/${collection}?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${collection}_export.${format === "xlsx" ? "xlsx" : "csv"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { log("Export failed:", e.message); }
  }, []);

  const exportCSV = useCallback((collection) => exportData(collection, "csv"), [exportData]);

  // Server-side filtering already applied. Keep for potential legacy fallback.
  const filteredUsers = users;

  const filteredBookings = bookings.filter((b) => {
    if (bookingFilter !== "all" && b.status !== bookingFilter) return false;
    if (!bookingSearch) return true;
    const q = bookingSearch.toLowerCase();
    return [
      b.user_name, b.user_phone, b.driver_name, b.driver_phone,
      b.id, b.booking_type, b.origin, b.destination, b.vehicle_brand, b.vehicle_model, b.plate_number,
      b.total_amount, b.created_at,
    ].some((v) => (v ?? "").toString().toLowerCase().includes(q));
  });
  const filteredPayments = payments.filter((p) => {
    if (paymentFilter !== "all" && p.method !== paymentFilter) return false;
    if (!paymentSearch) return true;
    const q = paymentSearch.toLowerCase();
    return [
      p.transaction_ref, p.phone_number, p.amount, p.booking_id, p.status, p.method, p.created_at,
    ].some((v) => (v ?? "").toString().toLowerCase().includes(q));
  });

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#0A2540]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <div className="bg-[#0A2540] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#FACC15]" />
              <h1 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Administration</h1>
            </div>
            <div className="flex items-center gap-2">
              <NotificationSoundSettings />
              <NotificationsActivateButton />
              <NotificationBell />
            </div>
          </div>
          <p className="text-sm text-white/60">Gerez votre plateforme TrigoTour</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-[#CBD5E1] mb-6 flex-wrap h-auto justify-start gap-y-1">
            <TabsTrigger value="stats" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-stats"><BarChart3 className="w-4 h-4 mr-1" /> Stats</TabsTrigger>
            <TabsTrigger value="drivers" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white relative" data-testid="admin-tab-drivers">
              <ShieldCheck className="w-4 h-4 mr-1" /> Chauffeurs
              {pendingDrivers.length > 0 && <span className="ml-1.5 bg-[#EF4444] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{pendingDrivers.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-1" /> Utilisateurs</TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-bookings"><Car className="w-4 h-4 mr-1" /> Reservations</TabsTrigger>
            <TabsTrigger value="vehicles" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-vehicles"><Truck className="w-4 h-4 mr-1" /> Vehicules</TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-payments"><CreditCard className="w-4 h-4 mr-1" /> Paiements</TabsTrigger>
            <TabsTrigger value="visits" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-visits"><Eye className="w-4 h-4 mr-1" /> Visites</TabsTrigger>
            <TabsTrigger value="tft-payments" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white relative" data-testid="admin-tab-tft-payments">
              <Phone className="w-4 h-4 mr-1" /> Paiements TFT
              {liveAlerts["tft-payments"] > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center animate-pulse shadow-md" data-testid="badge-tft-payments">
                  {liveAlerts["tft-payments"]}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="password-resets" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white relative" data-testid="admin-tab-password-resets">
              <KeyRound className="w-4 h-4 mr-1" /> Reset Mdp
              {liveAlerts["password-resets"] > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center animate-pulse shadow-md" data-testid="badge-password-resets">
                  {liveAlerts["password-resets"]}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-reports"><FileSpreadsheet className="w-4 h-4 mr-1" /> Rapports</TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-promo"><Sparkles className="w-4 h-4 mr-1" /> Promotions</TabsTrigger>
            <TabsTrigger value="gods-eye" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-gods-eye"><Eye className="w-4 h-4 mr-1" /> God&apos;s Eye</TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="promo-codes" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-promo-codes"><Sparkles className="w-4 h-4 mr-1" /> Codes Promo</TabsTrigger>
            )}
            <TabsTrigger value="payment-methods" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-payment-methods"><Wallet className="w-4 h-4 mr-1" /> Moyens paiement</TabsTrigger>
            <TabsTrigger value="pack-requests" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-pack-requests"><Hourglass className="w-4 h-4 mr-1" /> Packs heures</TabsTrigger>
            <TabsTrigger value="tft-stats" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-tft-stats"><BarChart3 className="w-4 h-4 mr-1" /> Stats TFT</TabsTrigger>
            <TabsTrigger value="audit" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-audit"><ShieldAlert className="w-4 h-4 mr-1" /> Audit support</TabsTrigger>
            <TabsTrigger value="pack-report" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-pack-report"><Hourglass className="w-4 h-4 mr-1" /> Rapport Packs</TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="pack-catalog" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-pack-catalog"><Hourglass className="w-4 h-4 mr-1" /> Catalogue Packs</TabsTrigger>
            )}
            <TabsTrigger value="express" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-express"><Zap className="w-4 h-4 mr-1" /> Trigo Express</TabsTrigger>
            <TabsTrigger value="express-drivers" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-express-drivers"><Zap className="w-4 h-4 mr-1" /> Chauffeurs Express</TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-partners"><Building2 className="w-4 h-4 mr-1" /> Partenaires</TabsTrigger>
            <TabsTrigger value="broadcast" className="data-[state=active]:bg-[#FACC15] data-[state=active]:text-[#0A2540]" data-testid="admin-tab-broadcast"><Bell className="w-4 h-4 mr-1" /> Communication</TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="admin-tab-referrals"><Users className="w-4 h-4 mr-1" /> Ambassadeurs</TabsTrigger>
          </TabsList>

          <TabsContent value="express">
            <div className="flex items-center justify-between mb-3 -mt-2">
              <span className="text-xs text-[#64748B]">Éditez les tarifs et types de véhicules Trigo Express.</span>
              <a href="/admin/express-live" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0F2B46] text-white text-xs font-bold hover:bg-[#173A5E]" data-testid="admin-open-express-live">
                <Zap className="w-3.5 h-3.5" /> Ouvrir le suivi en direct →
              </a>
            </div>
            <ExpressAdminPanel />
          </TabsContent>

          <TabsContent value="express-drivers">
            <ExpressDriversPanel />
          </TabsContent>

          <TabsContent value="promo">
            <PromoSettingsPanel />
          </TabsContent>

          <TabsContent value="gods-eye">
            <AdminGodsEye />
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="promo-codes">
              <AdminPromoCodesPanel />
            </TabsContent>
          )}

          <TabsContent value="payment-methods">
            <PaymentMethodsPanel />
          </TabsContent>

          <TabsContent value="pack-requests">
            <AdminPackRequests />
          </TabsContent>

          <TabsContent value="tft-stats">
            <AdminTFTStats />
          </TabsContent>

          <TabsContent value="audit">
            <AdminImpersonationLogs />
          </TabsContent>

          <TabsContent value="pack-report">
            <PackBookingsReport mode="admin" drivers={allDrivers} />
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="pack-catalog">
              <AdminPackCatalog />
            </TabsContent>
          )}

          <TabsContent value="stats">
            <StatsPanel stats={stats} onNavigate={setTab} />
          </TabsContent>

          <TabsContent value="drivers">
            <div className="flex justify-end mb-4">
              <ExportButton onExport={(fmt) => exportData("drivers", fmt)} label="Exporter chauffeurs" testId="export-drivers" />
            </div>
            <DriverManagement
              pendingDrivers={pendingDrivers}
              allDrivers={allDrivers}
              onValidate={validateDriver}
              onDelete={setDeleteConfirm}
              onImpersonate={handleImpersonate}
              actionLoading={actionLoading}
              onRefresh={fetchAll}
            />
          </TabsContent>

          <TabsContent value="users">
            <UserFilters userFilter={userFilter} setUserFilter={setUserFilter} userRoleFilter={userRoleFilter} setUserRoleFilter={setUserRoleFilter} onExport={() => exportCSV("users")} />
            <UsersTable
              users={filteredUsers}
              onToggle={toggleUser}
              onDelete={setDeleteConfirm}
              isSuperAdmin={isSuperAdmin}
              onRefresh={fetchUsers}
              loading={usersLoading}
              total={usersTotal}
              page={usersPage}
              pageSize={usersPageSize}
              totalPages={usersTotalPages}
              onPageChange={setUsersPage}
              onPageSizeChange={(s) => { setUsersPageSize(s); setUsersPage(1); }}
            />
          </TabsContent>

          <TabsContent value="bookings">
            <PromoLostCommissionKPI />
            <BookingFilters filter={bookingFilter} setFilter={setBookingFilter} search={bookingSearch} setSearch={setBookingSearch} onExport={() => exportCSV("bookings")} />
            <BookingsList bookings={filteredBookings} onRefresh={fetchAll} />
          </TabsContent>

          <TabsContent value="vehicles">
            <div className="flex justify-end mb-4">
              <ExportButton onExport={(fmt) => exportData("vehicles", fmt)} label="Exporter véhicules" testId="export-vehicles" />
            </div>
            <VehicleManagement vehicles={allVehicles} onRefresh={fetchAll} actionLoading={actionLoading} />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentFilters filter={paymentFilter} setFilter={setPaymentFilter} search={paymentSearch} setSearch={setPaymentSearch} onExport={() => exportCSV("payments")} />
            <PaymentsTable payments={filteredPayments} onRefresh={fetchAll} />
          </TabsContent>

          <TabsContent value="visits">
            <VisitStatsPanel />
          </TabsContent>

          <TabsContent value="tft-payments">
            <TFTPaymentRequestsPanel key={`tft-${liveAlerts["tft-payments"] || 0}`} />
          </TabsContent>

          <TabsContent value="password-resets">
            <PasswordResetsPanel key={`pwd-${liveAlerts["password-resets"] || 0}`} />
          </TabsContent>

          <TabsContent value="reports">
            <ReportsPanel onExport={exportData} />
          </TabsContent>

          <TabsContent value="partners">
            <AdminPartnersPanel />
          </TabsContent>

          <TabsContent value="broadcast">
            <AdminBroadcastPanel />
          </TabsContent>

          <TabsContent value="referrals">
            <AdminReferralLeaderboard />
          </TabsContent>
        </Tabs>
      </div>

      <DeleteConfirmDialog user={deleteConfirm} onConfirm={deleteUser} onCancel={() => setDeleteConfirm(null)} loading={actionLoading} />
    </div>
  );
}

function ReportsPanel({ onExport }) {
  const REPORTS = [
    { id: "drivers", label: "Chauffeurs", desc: "Liste complète des chauffeurs avec statut, TFT, revenus, notes", icon: ShieldCheck, color: "#0A2540" },
    { id: "bookings", label: "Réservations", desc: "Toutes les réservations avec statut, mode location, commission", icon: Car, color: "#3B82F6" },
    { id: "payments", label: "Paiements", desc: "Historique complet des paiements en espèces", icon: CreditCard, color: "#10B981" },
    { id: "tft", label: "Mouvements TFT", desc: "Recharges, débits, remboursements et retraits de tous les chauffeurs", icon: BarChart3, color: "#FACC15" },
    { id: "tft_payment_requests", label: "Demandes Mobile Money", desc: "Recharges TFT via MTN/Orange : références, statut, montants et bonus", icon: Phone, color: "#10B981" },
    { id: "vehicles", label: "Véhicules", desc: "Catalogue complet des véhicules en location", icon: Truck, color: "#8B5CF6" },
    { id: "rides", label: "Trajets covoiturage", desc: "Trajets publiés avec places et tarifs", icon: Car, color: "#06B6D4" },
    { id: "users", label: "Utilisateurs", desc: "Tous les utilisateurs (passagers + chauffeurs + admins)", icon: Users, color: "#64748B" },
    { id: "reviews", label: "Avis clients", desc: "Évaluations laissées par les passagers sur les chauffeurs", icon: BarChart3, color: "#EF4444" },
  ];
  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Centre de rapports</h2>
        <p className="text-sm text-white/70 mt-1">Téléchargez l&apos;ensemble des données de la plateforme aux formats CSV ou Excel pour vos analyses et reportings.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.id} className="bg-white border border-[#CBD5E1] rounded-2xl p-4 flex flex-col" data-testid={`report-card-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${r.color}15` }}>
                  <Icon className="w-5 h-5" style={{ color: r.color }} />
                </div>
                <p className="text-sm font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{r.label}</p>
              </div>
              <p className="text-xs text-[#64748B] flex-1 mb-3">{r.desc}</p>
              <ExportButton onExport={(fmt) => onExport(r.id, fmt)} label="Exporter" testId={`export-${r.id}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
